"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateForm() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType] = useState("Whole-Home");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [submissionStatus, setSubmissionStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [serverErrorMessage, setServerErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setZipError("");
    setConsentError("");
    setServerErrorMessage("");
    let hasError = false;

    // Validate 5-digit ZIP code
    const zipRegex = /^\d{5}$/;
    if (!zipRegex.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      hasError = true;
    }

    // Validate consent agreement
    if (!consent) {
      setConsentError("Please agree to the contact terms to continue.");
      hasError = true;
    }
    if (hasError) return;
    setSubmissionStatus("submitting");
    try {
      const payload = new URLSearchParams({
        name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        project_type: projectType,
        address: zip.trim(),
        zip: zip.trim(),
        details: message.trim(),
        timing: timing,
        budget: budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "whole-home-remodeling-2-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmissionStatus("success");
      } else {
        setSubmissionStatus("error");
        setServerErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setSubmissionStatus("error");
      setServerErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Heading */}
        <div className="text-center mb-10 lg:mb-12">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            Request a Consultation
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground">
            Plan Your Whole-Home Transformation
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Share the vision for your residential renovation. Our team will review
            your parameters and schedule an in-depth design consultation.
          </p>
        </div>

        {/* Glass Card Container */}
        <div className="rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 lg:p-12 transition-all">
          {submissionStatus === "success" ? <div className="py-12 px-4 text-center max-w-lg mx-auto">
              <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <CheckCircle2 className="size-8" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground mb-3">
                Estimate Request Received
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                Thank you for contacting Brookhaven Elevation Renovations. We have
                received your project information and our project coordinator will
                reach out shortly to confirm your consultation.
              </p>
              <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-4 py-2 rounded-full border border-border/60">
                <ShieldCheck className="size-4 text-primary" />
                <span>Your information is handled strictly for your consultation.</span>
              </div>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {submissionStatus === "error" && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Submission Notice</AlertTitle>
                  <AlertDescription>
                    {serverErrorMessage || "Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate."}
                  </AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-foreground">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="fullName" name="fullName" type="text" required placeholder="Jane Doe" value={fullName} onChange={e => setFullName(e.target.value)} className="bg-background/80" />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-foreground">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone" name="phone" type="tel" required placeholder="(404) 555-0192" value={phone} onChange={e => setPhone(e.target.value)} className="bg-background/80" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Email Address */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email" name="email" type="email" required placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} className="bg-background/80" />
                </div>

                {/* ZIP Code */}
                <div className="space-y-2">
                  <Label htmlFor="zip" className="text-foreground">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip" name="zip" type="text" required placeholder="30319" maxLength={5} value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} className={`bg-background/80 ${zipError ? "border-destructive ring-destructive/20 ring-2" : ""}`} />
                  {zipError && <p className="text-xs text-destructive font-medium mt-1">
                      {zipError}
                    </p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Project Type */}
                <div className="space-y-2">
                  <Label htmlFor="projectType" className="text-foreground">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="projectType" name="projectType" disabled value={projectType} className="border-input flex h-9 w-full rounded-md border bg-muted/60 px-3 py-1 text-sm shadow-xs transition-colors cursor-not-allowed opacity-90 text-foreground">
                    {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                {/* Preferred Timing */}
                <div className="space-y-2">
                  <Label htmlFor="timing" className="text-foreground">
                    Preferred Timing
                  </Label>
                  <select id="timing" name="timing" value={timing} onChange={e => setTiming(e.target.value)} className="border-input flex h-9 w-full rounded-md border bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] text-foreground">
                    <option value="">Select timeframe</option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                {/* Budget Range */}
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-foreground">
                    Budget Range
                  </Label>
                  <select id="budget" name="budget" value={budget} onChange={e => setBudget(e.target.value)} className="border-input flex h-9 w-full rounded-md border bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] text-foreground">
                    <option value="">Select budget</option>
                    {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Project Details */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-foreground">
                  Project Details / Message
                </Label>
                <Textarea id="message" name="message" rows={4} placeholder="Tell us about your home, current layout, and remodeling goals..." value={message} onChange={e => setMessage(e.target.value)} className="bg-background/80" />
              </div>

              {/* Consent Checkbox */}
              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" name="consent" checked={consent} onCheckedChange={checked => {
                setConsent(checked === true);
                if (consentError) setConsentError("");
              }} className={consentError ? "border-destructive ring-destructive/20 ring-2" : ""} />
                  <Label htmlFor="consent" className="text-xs sm:text-sm text-muted-foreground font-normal leading-relaxed cursor-pointer">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive font-medium mt-1.5 ml-7">
                    {consentError}
                  </p>}
              </div>

              {/* Submit CTA */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  Fields marked with <span className="text-destructive">*</span> are required.
                </p>
                <Button type="submit" disabled={submissionStatus === "submitting"} className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-7 py-3.5 rounded-lg shadow-sm hover:bg-primary/90 transition-all">
                  {submissionStatus === "submitting" ? <>
                      <Loader2 className="size-4 animate-spin" />
                      <span>Submitting...</span>
                    </> : <span>Request Estimate</span>}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}